import { Circle, CircleCheck, CircleDot, Minus, TriangleAlert } from "lucide-react";
import type { ComponentType } from "react";
import type { Etape, EtatEtape } from "@/lib/api";
import { cn } from "@/lib/utils";

/**
 * Les huit étapes d'un lot, rendues lisibles sans survol ni daltonisme.
 *
 * Une première version affichait huit pastilles colorées, le détail caché dans
 * une infobulle. Trois défauts, tous constatés à l'usage :
 *
 *  * la couleur portait l'information À ELLE SEULE — le beige « en cours » et
 *    le gris « à faire » sont séparés de quatre points de luminosité, donc
 *    indiscernables pour une partie des lecteurs, et pour tout le monde en
 *    plein soleil ;
 *  * le détail n'existait qu'au survol, geste qui n'existe pas sur le téléphone
 *    du poseur ;
 *  * rien ne disait ce que les couleurs signifiaient.
 *
 * Chaque état porte donc désormais une FORME, un MOT et son détail. Et les
 * étapes sans objet — celles dont les LOT ne sont pas développés — sont
 * repliées en une ligne : elles n'appellent aucune action, elles n'ont pas à
 * occuper huit lignes.
 */

type Apparence = {
  icone: ComponentType<{ className?: string }>;
  mot: string;
  couleur: string;
  marqueur: string;
};

export const APPARENCES: Record<EtatEtape, Apparence> = {
  TERMINEE: {
    icone: CircleCheck,
    mot: "terminée",
    couleur: "text-succes",
    marqueur: "text-succes",
  },
  EN_ALERTE: {
    icone: TriangleAlert,
    mot: "en alerte",
    couleur: "text-alerte",
    marqueur: "text-alerte",
  },
  EN_COURS: {
    icone: CircleDot,
    mot: "en cours",
    couleur: "text-encre",
    marqueur: "text-laiton-fonce",
  },
  A_FAIRE: {
    icone: Circle,
    mot: "à faire",
    couleur: "text-encre-douce",
    marqueur: "text-encre-douce",
  },
  NON_APPLICABLE: {
    icone: Minus,
    mot: "sans objet",
    couleur: "text-encre-douce",
    marqueur: "text-encre-douce/60",
  },
};

const ORDRE_LEGENDE: EtatEtape[] = ["EN_ALERTE", "EN_COURS", "A_FAIRE", "TERMINEE", "NON_APPLICABLE"];

export function LegendeEtapes() {
  return (
    <div className="flex flex-wrap items-center gap-x-5 gap-y-2 text-xs text-encre-douce">
      <span className="font-medium">Les étapes :</span>
      {ORDRE_LEGENDE.map((etat) => {
        const { icone: Icone, mot, marqueur } = APPARENCES[etat];
        return (
          <span key={etat} className="inline-flex items-center gap-1.5">
            <Icone className={cn("h-3.5 w-3.5 shrink-0", marqueur)} aria-hidden />
            {mot}
          </span>
        );
      })}
    </div>
  );
}

export function Timeline({
  etapes,
  prochaineAction,
}: {
  etapes: Etape[];
  prochaineAction: Etape | null;
}) {
  const actives = etapes.filter((e) => e.etat !== "NON_APPLICABLE");
  const sansObjet = etapes.filter((e) => e.etat === "NON_APPLICABLE");

  return (
    <div className="flex flex-col gap-2">
      {actives.map((etape) => {
        const { icone: Icone, mot, couleur, marqueur } = APPARENCES[etape.etat];
        const estProchaine = prochaineAction?.etape === etape.etape;
        return (
          <div
            key={etape.etape}
            className={cn(
              "flex gap-2.5 rounded-[6px] py-1",
              estProchaine && "border-l-2 border-laiton bg-chene-clair/25 py-2 pl-2.5",
            )}
          >
            <Icone className={cn("mt-0.5 h-4 w-4 shrink-0", marqueur)} aria-hidden />
            <div className="min-w-0">
              <p className={cn("text-sm font-medium", couleur)}>
                {etape.libelle}
                <span className="font-normal text-encre-douce"> — {mot}</span>
                {estProchaine ? (
                  <span className="ml-2 text-xs font-medium text-laiton-fonce">
                    prochaine action
                  </span>
                ) : null}
              </p>
              {etape.detail ? (
                <p className="text-sm text-encre-douce">{etape.detail}</p>
              ) : null}
            </div>
          </div>
        );
      })}

      {sansObjet.length > 0 ? (
        <p className="flex gap-2.5 text-sm text-encre-douce/80">
          <Minus className="mt-0.5 h-4 w-4 shrink-0 text-encre-douce/60" aria-hidden />
          <span>
            {sansObjet.length === 1 ? "Étape sans objet" : `${sansObjet.length} étapes sans objet`}{" "}
            : {sansObjet.map((e) => e.libelle).join(", ")}.
          </span>
        </p>
      ) : null}
    </div>
  );
}
